import {
  useAltKeyState,
  useCurrentPreviewPinId,
  useSetCurrentPreviewPinId,
} from "@/utils/hooks/useAltKeyManager";
import { useMemoizedFn } from "ahooks";
import { useEffect, useState } from "react";

/**
 * 放大镜动画阶段
 * - hidden: 隐藏状态，组件未挂载
 * - entering: 正在放大（播放放大动画）
 * - visible: 显示状态，动画已完成
 * - exiting: 正在缩小（播放缩小动画）
 */
export type MagnifierPhase = "hidden" | "entering" | "visible" | "exiting";

interface UseMagnifierStateOptions {
  pinId: string;
  isVideo: boolean;
  showMagnifier: boolean; // AB 实验控制
}

interface UseMagnifierStateResult {
  /** 当前动画阶段 */
  phase: MagnifierPhase;
  /** 是否应该渲染组件（非 hidden 阶段都渲染） */
  shouldRender: boolean;
  /** 动画结束回调，需要绑定到组件的 onAnimationEnd */
  handleAnimationEnd: () => void;
  /** 鼠标进入图片区域 */
  handleImageMouseEnter: () => void;
  /** 鼠标离开图片区域 */
  handleImageMouseLeave: () => void;
  /** 鼠标进入放大框 */
  handlePreviewMouseEnter: () => void;
  /** 鼠标离开放大框 */
  handlePreviewMouseLeave: () => void;
}

/**
 * 放大镜状态机 Hook
 *
 * 状态流转：
 * - Alt+hover: hidden → entering
 * - 动画结束: entering → visible
 * - Alt释放/鼠标离开: visible → exiting
 * - 动画结束: exiting → hidden
 *
 * 特殊情况：
 * - exiting 时重新触发: exiting → entering (动画反转)
 * - 切换到其他卡片: 任何状态 → hidden (立即清除)
 */
export function useMagnifierState({
  pinId,
  isVideo,
  showMagnifier,
}: UseMagnifierStateOptions): UseMagnifierStateResult {
  const [phase, setPhase] = useState<MagnifierPhase>("hidden");
  const [previewHover, setPreviewHover] = useState(false);
  const [localImageHover, setLocalImageHover] = useState(false);

  const altKeyPressed = useAltKeyState();
  const currentPreviewPinId = useCurrentPreviewPinId();
  const setCurrentPreviewPinId = useSetCurrentPreviewPinId();

  // 计算是否应该显示放大镜
  const shouldShow =
    showMagnifier && altKeyPressed && localImageHover && !isVideo;

  // 处理显示/隐藏逻辑
  useEffect(() => {
    if (shouldShow) {
      // 应该显示
      if (phase === "hidden" || phase === "exiting") {
        setPhase("entering");
        setCurrentPreviewPinId(pinId);
      }
    } else if (!altKeyPressed) {
      // Alt 键释放
      if (phase === "entering" || phase === "visible") {
        setPhase("exiting");
      }
    }
  }, [shouldShow, altKeyPressed, phase, pinId, setCurrentPreviewPinId]);

  // 监听 currentPreviewPinId 变化，如果不是当前卡片，则立即隐藏
  useEffect(() => {
    if (currentPreviewPinId !== null && currentPreviewPinId !== pinId) {
      // 其他卡片正在预览，立即隐藏自己
      if (phase !== "hidden") {
        setPhase("hidden");
        setPreviewHover(false);
      }
    }
  }, [currentPreviewPinId, pinId, phase]);

  // 鼠标离开图片区域的处理
  useEffect(() => {
    if (!localImageHover && !previewHover && !altKeyPressed) {
      // 鼠标既不在图片上，也不在放大框上，且 Alt 未按下
      if (phase === "visible" || phase === "entering") {
        setPhase("exiting");
      }
    }
  }, [localImageHover, previewHover, altKeyPressed, phase]);

  // 动画结束回调
  const handleAnimationEnd = useMemoizedFn(() => {
    if (phase === "entering") {
      setPhase("visible");
    } else if (phase === "exiting") {
      setPhase("hidden");
      setPreviewHover(false);
    }
  });

  // 鼠标事件处理
  const handleImageMouseEnter = useMemoizedFn(() => {
    setLocalImageHover(true);
  });

  const handleImageMouseLeave = useMemoizedFn(() => {
    setLocalImageHover(false);
  });

  const handlePreviewMouseEnter = useMemoizedFn(() => {
    setPreviewHover(true);
  });

  const handlePreviewMouseLeave = useMemoizedFn(() => {
    setPreviewHover(false);
    // 鼠标离开放大框时，如果也不在原图片上且 Alt 未按下，则开始退出动画
    if (!localImageHover && !altKeyPressed) {
      if (phase === "visible" || phase === "entering") {
        setPhase("exiting");
      }
    }
  });

  return {
    phase,
    shouldRender: phase !== "hidden",
    handleAnimationEnd,
    handleImageMouseEnter,
    handleImageMouseLeave,
    handlePreviewMouseEnter,
    handlePreviewMouseLeave,
  };
}
